import { useState } from "react";
import { useNavigate, useLocation, Navigate, Link } from "react-router-dom";
import { Loader2, Lock, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { LOGO_URL } from "@/lib/content";
import { portalLogin, getPortalToken } from "@/lib/portalApi";

export default function PortalLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  if (getPortalToken()) return <Navigate to="/portal" replace />;
  const from = loc.state?.from?.pathname || "/portal";

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !email || !password) return;
    setSubmitting(true);
    try {
      await portalLogin(email.trim(), password);
      toast.success("Welcome back.");
      nav(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Login failed.";
      toast.error(typeof detail === "string" ? detail : "Login failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="portal-login-page"
      className="min-h-screen bg-black text-[#F5F5F0] grain flex items-center justify-center px-6"
    >
      <Link
        to="/"
        data-testid="portal-login-back-home"
        className="fixed top-5 left-5 md:top-8 md:left-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#A3A3A3] hover:text-[#D9A6F5] transition-colors z-10"
      >
        <ArrowLeft size={14} /> Back to home
      </Link>
      <div className="relative z-[2] w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img src={LOGO_URL} alt="Swingle" className="h-24 md:h-28 w-auto object-contain" />
        </div>
        <div className="brackets bg-[#0A0A0A] border border-white/10 p-10">
          <div className="flex items-center justify-center gap-3 text-xs uppercase tracking-[0.3em] text-[#D9A6F5] mb-4">
            <Lock size={14} /> Venue Portal
          </div>
          <h1 className="text-center text-3xl md:text-4xl font-black tracking-tighter mb-8">
            Sign in
          </h1>
          <form onSubmit={submit} className="space-y-5" data-testid="portal-login-form" noValidate>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">
                Email
              </label>
              <Input
                type="email"
                autoFocus
                autoComplete="email"
                data-testid="portal-login-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-xs uppercase tracking-[0.22em] text-[#A3A3A3] font-medium">
                Password
              </label>
              <Input
                type="password"
                autoComplete="current-password"
                data-testid="portal-login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/15 rounded-none h-12 text-[#F5F5F0] focus-visible:ring-[#D9A6F5] focus-visible:ring-offset-0"
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting || !email || !password}
              data-testid="portal-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-[#D9A6F5] text-black px-8 py-4 text-sm uppercase tracking-[0.22em] font-bold hover:bg-[#E8C8FF] disabled:opacity-60 transition-colors"
            >
              {submitting ? <Loader2 size={16} className="animate-spin" /> : null}
              Sign In
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
